
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useCancellationRequests = (isAdmin: boolean) => {
  const [cancellationRequests, setCancellationRequests] = useState<any[]>([]);
  const { toast } = useToast();

  const fetchCancellationRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('cancellation_requests')
        .select(`
          *,
          bookings (
            id,
            start_time,
            end_time,
            parking_spot:parking_spots (
              address,
              owner_name
            )
          ),
          profiles:user_id (
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCancellationRequests(data || []);
    } catch (error) {
      console.error('Error fetching cancellation requests:', error);
      toast({
        title: "Error",
        description: "Failed to load cancellation requests",
        variant: "destructive",
      });
    }
  };

  const handleCancellationAction = async (requestId: string, bookingId: string, action: 'approve' | 'reject') => {
    try {
      if (action === 'approve') {
        const { error: bookingError } = await supabase
          .from('bookings')
          .update({ status: 'cancelled' })
          .eq('id', bookingId);

        if (bookingError) throw bookingError;
      }

      const { error: requestError } = await supabase
        .from('cancellation_requests')
        .update({ status: action === 'approve' ? 'approved' : 'rejected' })
        .eq('id', requestId);

      if (requestError) throw requestError;

      toast({
        title: "Success",
        description: `Cancellation request ${action === 'approve' ? 'approved' : 'rejected'} successfully.`,
      });

      await fetchCancellationRequests();
    } catch (error) {
      console.error(`Error ${action}ing cancellation request:`, error);
      toast({
        title: "Error",
        description: `Failed to ${action} cancellation request`,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchCancellationRequests();
    }
  }, [isAdmin]);

  return { cancellationRequests, handleCancellationAction };
};
